import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AnimationOnScroll from "react-animate-on-scroll";
import "animate.css/animate.min.css";
import BackArrow from "../assets/images/back-arrow.svg";
import CameraIcon from "../assets/images/camera-icon-black.svg";
import UploadSuccess from "../assets/images/upload-success.svg";
import RefreshIcon from "../assets/images/refresh-icon.svg";
import UploadCheck from "../assets/images/upload-check.svg";
import CaptureIcon from "../assets/images/capture-icon.svg";
import RemoveIcon from "../assets/images/remove.svg";
import SmileIcon from "../assets/images/front-teeth.png";
import UpperTeeth from "../assets/images/upper-teeth1.png";
import LowerTeeth from "../assets/images/lower-teeth1.png";
import LeftTeeth from "../assets/images/left-teeth.png";
import RightTeeth from "../assets/images/right-teeth.png";
import imageCompression from "browser-image-compression";

import {
  derma_api_key,
  derma_api_secret_key,
  sdkUnityURL,
} from "../utils/api-url-list";
import { ReactComponent as Loader } from "../assets/icons/Loader.svg";

const photoChecklist = [
  {
    id: "front",
    label: "Front Smile",
    required: true,
    guide: "Smile naturally showing your front teeth",
  },
  {
    id: "upper",
    label: "Upper Teeth",
    required: true,
    guide: "Open mouth showing upper teeth",
  },
  {
    id: "lower",
    label: "Lower Teeth",
    required: false,
    guide: "Open mouth showing lower teeth",
  },
  {
    id: "left",
    label: "Left Side Bite",
    required: false,
    guide: "Side view showing left alignment",
  },
  {
    id: "right",
    label: "Right Side Bite",
    required: false,
    guide: "Side view showing right alignment",
  },
];

const photoInstructions = {
  front: [
    "Smile naturally showing your front teeth",
    "Keep lips relaxed and slightly parted",
    "Look directly at the camera",
    "Ensure even lighting on your face",
  ],
  upper: [
    "Open your mouth wide",
    "Lift your upper lip gently",
    "Show upper teeth and gum line clearly",
    "Keep tongue down and relaxed",
  ],
};

export default function DentalAnalyzer() {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [capturedPhotos, setCapturedPhotos] = useState({});
  const [activeAngle, setActiveAngle] = useState("front");
  const [tempCaptured, setTempCaptured] = useState(null);
  const [cameraError, setCameraError] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [activePreview, setActivePreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const EMPLOYEE_ID = "7596979596";

  async function getPresignedUrl(fileName) {
    const res = await fetch(sdkUnityURL("/dental/upload-image"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: derma_api_secret_key,
      },
      body: JSON.stringify({
        employee_id: EMPLOYEE_ID,
        api_key: derma_api_key,
        action: "upload",
        file_name: fileName,
      }),
    });

    if (!res.ok) {
      throw new Error(
        `Failed to fetch presigned URL: ${res.status} ${res.statusText}`
      );
    }

    return res.json();
  }

  async function uploadToS3(presignedData, fileBlob) {
    let res;
    if (presignedData.presigned_post) {
      const { url, fields } = presignedData.presigned_post;
      const formData = new FormData();
      Object.entries(fields).forEach(([key, value]) => {
        formData.append(key, value);
      });
      formData.append("file", fileBlob);
      res = await fetch(url, {
        method: "POST",
        body: formData,
      });
    } else if (presignedData.upload_url) {
      res = await fetch(presignedData.upload_url, {
        method: "PUT",
        headers: {
          "Content-Type": fileBlob.type || "application/octet-stream",
          Authorization: derma_api_secret_key,
        },
        body: fileBlob,
      });
    } else {
      throw new Error("Invalid presigned data format from API");
    }

    if (!res.ok) {
      throw new Error(
        `Failed to upload image to S3: ${res.status} ${res.statusText}`
      );
    }
    return presignedData.public_url || null;
  }

  // Send uploaded image URLs for dental analysis
  async function analyzePhotos(imageUrls) {
    const res = await fetch(sdkUnityURL("/dental/dental-assessment"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: derma_api_secret_key,
      },
      body: JSON.stringify({
        employee_id: EMPLOYEE_ID,
        api_key: derma_api_key,
        action: "assesment",
        image_urls: imageUrls,
        // notes: {
        //   patient_name: "John Doe",
        //   remarks: "Initial dental assessment",
        // },
      }),
    });

    if (!res.ok) {
      throw new Error(
        `Failed to analyze photos: ${res.status} ${res.statusText}`
      );
    }

    return res.json();
  }

  useEffect(() => {
    const handleClickOutside = () => setActivePreview(null);
    window.addEventListener("click", handleClickOutside);
    return () => window.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
    if (showModal) {
      startCamera();
    } else {
      stopCamera();
    }
  }, [showModal]);

  useEffect(() => {
    setTempCaptured(null);
  }, [activeAngle]);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setCameraError(false);
    } catch (err) {
      console.error("Camera Error:", err);
      setCameraError(true);
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach((track) => track.stop());
      videoRef.current.srcObject = null;
    }
  };

  const capture = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (video && canvas) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas
        .getContext("2d")
        .drawImage(video, 0, 0, canvas.width, canvas.height);
      const imageData = canvas.toDataURL("image/jpeg");
      setTempCaptured(imageData);
    }
  };

  const uploadCaptured = () => {
    setCapturedPhotos((prev) => ({ ...prev, [activeAngle]: tempCaptured }));
    setTempCaptured(null);
    startCamera();
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCapturedPhotos((prev) => ({
          ...prev,
          [activeAngle]: reader.result,
        }));
        setShowModal(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const requiredPhotosUploaded =
    capturedPhotos["front"] !== undefined &&
    capturedPhotos["upper"] !== undefined;

  const completedCount = Object.keys(capturedPhotos).length;

  const requiredPhotos = photoChecklist.filter((p) => p.required);
  const optionalPhotos = photoChecklist.filter((p) => !p.required);
  const navigate = useNavigate();

  const handleAnalyzePhotos = async () => {
    setIsUploading(true);
    try {
      const imageUrls = [];
      const photoEntries = Object.entries(capturedPhotos);
      const compressionOptions = {
        maxSizeMB: 5,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
      };

      for (const [key, dataUrl] of photoEntries) {
        const originalBlob = await (await fetch(dataUrl)).blob();
        const compressedFile = await imageCompression(
          originalBlob,
          compressionOptions
        );

        const fileName = `${key}_${Date.now()}.jpg`;
        const presignedData = await getPresignedUrl(fileName);

        if (presignedData.presigned_post) {
          const { url, fields } = presignedData.presigned_post;
          const formData = new FormData();

          Object.entries(fields).forEach(([fieldKey, value]) => {
            formData.append(fieldKey, value);
          });
          formData.append("file", compressedFile);

          const s3Resp = await fetch(url, {
            method: "POST",
            body: formData,
          });

          if (!s3Resp.ok) {
            throw new Error("Upload to S3 failed.");
          }

          const publicUrl = `${url}${fields.key}`;
          imageUrls.push(publicUrl);
        } else {
          const publicUrl = await uploadToS3(presignedData, compressedFile);
          if (publicUrl) {
            imageUrls.push(publicUrl);
          }
        }
      }

      if (imageUrls.length > 0) {
        const dentalAnalysis = await analyzePhotos(imageUrls);
        if (dentalAnalysis.statusCode?.toString().startsWith("2")) {
          navigate("/dental-report", { state: { dentalAnalysis } });
        } else {
          setErrorMessage(dentalAnalysis.message);
        }
      } else {
        throw new Error("No images were successfully uploaded.");
      }
    } catch (err) {
      console.error("Error in analyzing photos:", err);
      alert("An error occurred during processing. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="max-w-md lg:max-w-[80%] md:max-w-[80%] mx-auto">
      {/* Header */}
      <div className="flex mb-5 px-6 mx-6 relative z-50">
        <Link
          to={-1}
          className="w-9 h-9 bg-[#2D2D2D] rounded-full flex justify-center items-center"
        >
          <img src={BackArrow} alt="Back" />
        </Link>
        <div className="ml-4 -mt-1">
          <h1 className="text-lg font-semibold">
            Dental <span className="text-secondary">Scan</span>
          </h1>
          <p className="text-xs text-white/85">
            Comprehensive oral health analysis
          </p>
        </div>
      </div>
      {/* Background Blur */}
      <div className="circle-blur w-[75%] h-64 mx-auto mt-10 blur-[90px] absolute left-0 right-0 top-10"></div>
      {/* Progress */}
      <div className="px-6 w-full mx-6">
        <div className="bg-[#1D1D1D]/70 rounded-[20px] px-4 pt-5 pb-6 mb-2 relative z-50">
          <div className="flex justify-between items-center mb-2">
            <h6 className="text-xs font-semibold">Capture Progress</h6>
            <div className="flex items-center gap-2">
              <p
                className={`text-xxs ${
                  completedCount >= 2 ? "text-green-600" : ""
                }`}
              >
                {completedCount}/5 photos
              </p>
              {completedCount >= 2 && (
                <span className="text-xxs text-green-600 font-medium">
                  Minimum Met
                </span>
              )}
            </div>
          </div>

          <div className="w-full bg-white/50 rounded-full h-1 mt-1">
            <div
              className={`h-1 rounded-full transition-all duration-300 ${
                completedCount >= 2 ? "bg-green-500" : "bg-orange-400"
              }`}
              style={{ width: `${(completedCount / 5) * 100}%` }}
            ></div>
          </div>

          <p className="text-xxs mt-3 text-white/85 leading-3">
            Capture at least 2 "Required" photos (marked with ⭐) to enable
            analysis.
          </p>
        </div>
      </div>
      {/* camera modal section */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-center items-center px-4">
          <div className="bg-white rounded-xl w-full max-w-lg p-4 relative shadow-lg">
            <button
              className="absolute top-2 right-3 text-xl text-gray-600"
              onClick={() => {
                setShowModal(false);
                setTempCaptured(null);
                stopCamera();
              }}
            >
              &times;
            </button>
            <h4 className="text-sm font-medium text-primary mb-3">
              Capture Image
            </h4>
            {/* Camera View */}
            <div className="w-full h-80 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center">
              {cameraError ? (
                <p className="text-red-500 text-sm text-center px-4">
                  Camera access was denied or unavailable. Check your browser
                  settings.
                </p>
              ) : tempCaptured ? (
                <img
                  src={tempCaptured}
                  alt="Preview"
                  className="w-full h-full object-cover"
                />
              ) : (
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                />
              )}
              <canvas ref={canvasRef} style={{ display: "none" }} />
            </div>

            {/* Camera Controls */}
            <div className="flex justify-center gap-4 mt-2 w-full">
              {!tempCaptured && !cameraError && (
                <button
                  onClick={capture}
                  className="w-12 h-12 rounded-full bg-blue-50 mt-2 border border-blue-200 text-white text-xl flex items-center justify-center"
                >
                  <img src={CaptureIcon} className="w-5 h-5" alt="" />
                </button>
              )}
              {tempCaptured && (
                <>
                  <div className="flex items-center justify-between w-full mt-2">
                    <button
                      onClick={() => {
                        setTempCaptured(null);
                        startCamera();
                      }}
                      className="text-xs text-primary w-[48%] px-2 h-9 rounded-lg border border-primary/30 flex items-center justify-center font-medium"
                    >
                      <img src={RefreshIcon} className="w-4 h-4 mr-3" alt="" />
                      Retake Photo
                    </button>
                    <button
                      onClick={() => {
                        uploadCaptured();
                        setShowModal(false);
                      }}
                      className="px-4 py-2 bg-green-600 text-white h-9 rounded-lg text-xs font-medium w-[48%] flex items-center justify-center"
                    >
                      <img src={UploadCheck} className="w-4 h-4 mr-3" alt="" />{" "}
                      Upload Photo
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* File Upload */}
            <div className="text-center mt-4">
              <label className="block text-sm mb-3 font-semibold text-primary">
                Upload from File
              </label>

              <label
                htmlFor="file-upload"
                className="flex w-full items-center justify-center gap-2 h-12 bg-primary text-white text-sm font-medium rounded-full cursor-pointer hover:bg-blue-700 transition"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#fff"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-upload w-4 h-4 mr-2"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="17 8 12 3 7 8"></polyline>
                  <line x1="12" x2="12" y1="3" y2="15"></line>
                </svg>
                Choose Photos
              </label>

              <input
                id="file-upload"
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                className="hidden"
              />
            </div>
          </div>
        </div>
      )}
      {/* end camera modal section */}
      {/* Captured Preview */}
      <div className="mt-4 px-6 pb-6 relative z-10 mx-6">
        <h3 className="mb-2 text-sm font-medium">Captured Photos</h3>

        {/* No Image Message */}
        {Object.keys(capturedPhotos).length === 0 ? (
          <p className="text-xs text-gray-500">No image available</p>
        ) : (
          <div className="grid grid-cols-4 gap-4">
            {Object.entries(capturedPhotos).map(([key, src]) => (
              <div key={key} className="relative group">
                <img
                  src={src}
                  alt={key}
                  className="rounded-lg w-full h-36 object-cover cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActivePreview(key);
                  }}
                />
                <span className="absolute bottom-1 left-1 bg-black/90 text-white text-xs px-2 rounded">
                  {photoChecklist.find((p) => p.id === key)?.label}
                </span>

                {activePreview === key && (
                  <div className="absolute inset-0 bg-black bg-opacity-60 flex flex-col justify-center items-center gap-2 rounded-lg">
                    <button
                      onClick={() => {
                        setActiveAngle(key);
                        setShowModal(true); // Retake
                        setActivePreview(null);
                      }}
                      className="text-primary text-xs font-medium bg-white px-3 py-1 rounded-md flex items-center"
                    >
                      <img src={RefreshIcon} className="w-3 h-3 mr-2" alt="" />
                      Retake
                    </button>
                    <button
                      onClick={() => {
                        const updated = { ...capturedPhotos };
                        delete updated[key];
                        setCapturedPhotos(updated); // Remove
                        setActivePreview(null);
                      }}
                      className="text-white text-xs bg-red-600 font-medium pl-2 pr-3 py-1 rounded-md flex items-center"
                    >
                      <img src={RemoveIcon} className="w-3 h-3 mr-2" alt="" />
                      Remove
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="lg:px-6 md:px-6 px-0 w-full mx-6">
        <div className="bg-white px-6 pt-6 pb-20 lg:rounded-tl-2xl lg:rounded-tr-2xl md:rounded-tl-2xl md:rounded-tr-2xl">
          {/* Required Photos Section */}
          <div className="w-full">
            <AnimationOnScroll animateIn="animate__fadeInUp" duration={0.65}>
              <div className="flex items-center mb-3">
                <h3 className="font-semibold text-base text-primary">
                  Required Photos
                </h3>
                <span className="bg-[#fefcec] text-xxs font-medium text-[#bd6834] ml-4 w-[70px] leading-5 h-5 px-1 flex text-center justify-center rounded-full border border-[#cab33b]">
                  Minimum
                </span>
              </div>
            </AnimationOnScroll>
            <div className="flex justify-between lg:justify-start md:justify-start space-x-2 lg:space-x-5 md:space-x-5 relative">
              {requiredPhotos.map((item) => (
                <AnimationOnScroll
                  animateIn="animate__fadeInUp"
                  duration={0.65}
                >
                  <button
                    key={item.id}
                    onClick={() => {
                      setActiveAngle(item.id);
                      setShowModal(true);
                    }}
                    className={`w-full relative px-3 py-2 rounded-lg border text-left flex flex-col flex-wrap justify-between items-center mb-2
        ${
          activeAngle === item.id
            ? "border-blue-500 bg-blue-50 text-primary"
            : "border-gray-400 text-primary bg-[#fbfbfb]"
        }
        ${capturedPhotos[item.id] ? "bg-green-50 border-green-300" : ""}`}
                  >
                    <div className="flex flex-col">
                      <div className="flex items-center mb-2">
                        <span className="text-xs text-gray-500">
                          {capturedPhotos[item.id] ? (
                            <img
                              src={UploadSuccess}
                              alt=""
                              className="w-5 h-5 mr-2"
                            />
                          ) : (
                            <img
                              src={CameraIcon}
                              className="w-5 h-5 mr-2"
                              alt=""
                            />
                          )}
                        </span>
                        <div className="text-xs font-semibold">
                          {item.label}
                        </div>
                      </div>

                      <div className="text-xxs text-primary leading-3">
                        {item.guide}
                      </div>
                    </div>
                  </button>
                </AnimationOnScroll>
              ))}
            </div>
          </div>

          {/* Additional Photos Section */}
          <div className="mt-4">
            <AnimationOnScroll animateIn="animate__fadeInUp" duration={0.65}>
              <h3 className="font-semibold text-base text-primary mb-3">
                Additional Photos
              </h3>
            </AnimationOnScroll>
            <div className="grid grid-cols-3 gap-4">
              {optionalPhotos.map((item) => (
                <AnimationOnScroll
                  animateIn="animate__fadeInUp"
                  duration={0.65}
                >
                  <button
                    key={item.id}
                    disabled={!requiredPhotosUploaded}
                    onClick={() => {
                      if (!requiredPhotosUploaded) return;
                      setActiveAngle(item.id);
                      setShowModal(true);
                    }}
                    className={`w-full px-3 py-2 rounded-lg border text-left flex flex-col flex-wrap justify-between items-center lg:items-start md:items-start mb-3
        ${
          activeAngle === item.id
            ? "border-blue-500 bg-blue-50"
            : "border-gray-400 bg-[#fbfbfb]"
        }
        ${capturedPhotos[item.id] ? "bg-green-50 border-green-300" : ""}
        ${!requiredPhotosUploaded ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    <div className="flex flex-col">
                      <div className="flex items-center mb-2">
                        <span className="text-xs text-gray-500">
                          {capturedPhotos[item.id] ? (
                            <img
                              src={UploadSuccess}
                              alt=""
                              className="w-5 h-5 mr-2"
                            />
                          ) : (
                            <img
                              src={CameraIcon}
                              className="w-5 h-5 mr-2"
                              alt=""
                            />
                          )}
                        </span>
                        <div className="text-xs font-semibold text-primary">
                          {item.label}
                        </div>
                      </div>

                      <div className="text-xxs text-primary leading-3">
                        {item.guide}
                      </div>
                    </div>
                  </button>
                </AnimationOnScroll>
              ))}
            </div>
          </div>

          {/* Guide and Tips */}
          <div className="w-full mt-2 mb-3">
            <AnimationOnScroll animateIn="animate__fadeInUp" duration={0.65}>
              <h5 className="text-base font-semibold text-primary">
                {photoChecklist.find((p) => p.id === activeAngle)?.label} View
              </h5>
              <p className="text-xs text-primary/85 mb-4 relative z-10">
                {photoChecklist.find((p) => p.id === activeAngle)?.guide}
              </p>
            </AnimationOnScroll>
            {/* Position Guide Image */}
            <div className="grid grid-cols-2 gap-10 w-full">
              {/* Position Guide Text */}
              <AnimationOnScroll animateIn="animate__fadeInUp" duration={0.65}>
                <div className="px-4 py-5 bg-[#232831] rounded-2xl relative">
                  <div className=" bg-blue-600 w-20 h-20 blur-[50px] absolute mt-10 left-[40%] z-10"></div>

                  <div className="w-full flex justify-center mb-3">
                    <img
                      src={
                        activeAngle === "front"
                          ? SmileIcon
                          : activeAngle === "upper"
                          ? UpperTeeth
                          : activeAngle === "lower"
                          ? LowerTeeth
                          : activeAngle === "left"
                          ? LeftTeeth
                          : activeAngle === "right"
                          ? RightTeeth
                          : SmileIcon
                      }
                      alt="Position Guide"
                      className="w-full rounded-xl"
                    />
                  </div>

                  <h3 className="font-semibold mb-1 text-secondary text-sm text-center relative z-10">
                    Position Guide
                  </h3>
                  <p className="text-xs text-white text-center relative z-10">
                    {photoChecklist.find((p) => p.id === activeAngle)?.guide}
                  </p>
                </div>
              </AnimationOnScroll>
              {/* Instructions */}
              <div className="bg-white mt-3 space-y-2">
                <AnimationOnScroll
                  animateIn="animate__fadeInUp"
                  duration={0.65}
                >
                  <h4 className="font-semibold text-sm text-primary">
                    How to take this photo:
                  </h4>
                </AnimationOnScroll>

                <ul className="list-decimal list-inside text-xs text-primary">
                  {(photoInstructions[activeAngle] || []).map((tip, idx) => (
                    <AnimationOnScroll
                      animateIn="animate__fadeInUp"
                      duration={0.65}
                    >
                      <li key={idx} className="mb-1">
                        {tip}
                      </li>
                    </AnimationOnScroll>
                  ))}
                </ul>
              </div>
            </div>
          </div>
          {/* Analyze Button */}
          <AnimationOnScroll animateIn="animate__fadeInUp" duration={0.65}>
            {!requiredPhotosUploaded && (
              <p className="text-center text-red-500 text-xs mt-10">
                Please capture at least 2 photos to enable analysis.
              </p>
            )}
            {errorMessage?.length > 0 && (
              <p className="mt-1.5 text-xxs mx-auto text-center text-error">
                {errorMessage}
              </p>
            )}
            <div className="grid grid-cols-2 gap-4 mb-10">
              <button
                className={`w-full py-2 rounded-full text-white h-12 mt-4 text-sm flex items-center justify-center gap-2 ${
                  requiredPhotosUploaded ? "bg-primary" : "bg-gray-400"
                }`}
                disabled={!requiredPhotosUploaded || isUploading}
                onClick={handleAnalyzePhotos}
              >
                {isUploading ? (
                  <>
                    <span>Uploading...</span>
                    <Loader className="shrink-0 h-4 w-4 ml-2" />
                  </>
                ) : (
                  `Analyze Photos (${completedCount} Image${
                    completedCount !== 1 ? "s" : ""
                  })`
                )}
              </button>

              <button
                onClick={() => {
                  setCapturedPhotos({});
                  setTempCaptured(null);
                  setActiveAngle("front");
                  setCameraError(false);
                  startCamera();
                }}
                className="mt-4 w-full py-2 border border-primary/30 rounded-full h-12 text-sm text-primary"
              >
                Start Over
              </button>
            </div>
          </AnimationOnScroll>
        </div>
      </div>
    </div>
  );
}
